import { SpineView } from '@wiener/rn-spine';
import React, { useEffect, useRef, useState } from 'react';
import { Animated, Modal, Platform, StyleSheet, useWindowDimensions, View } from 'react-native';

import { SPINE_ASSETS } from '../../../../assets/spine';
import { HEADER_WALLET_CHIP_CENTER, HeaderWallet } from '../../../shell/header/HeaderWallet';

type Point = { x: number; y: number };

type Props = {
	request: {
		id: number;
		source: Point & { scale?: number };
		wallet: Point & { scale?: number };
		amount: number;
		initialChips: number;
	} | null;
	/** Renders in a native window layer when the caller is not already full-screen. */
	fullScreen?: boolean;
};

// The handoff skeleton uses a centre-origin coordinate system with y increasing upward.
export const DailyMissionClaimAnimation = ({ request, fullScreen = false }: Props) => {
	const { width, height } = useWindowDimensions();
	const [displayChips, setDisplayChips] = useState(0);
	const [displayRequestId, setDisplayRequestId] = useState<number | null>(null);
	const walletOpacity = useRef(new Animated.Value(0)).current;
	useEffect(() => {
		if (!request) return;
		const initialChips = request.initialChips;
		setDisplayChips(initialChips);
		setDisplayRequestId(request.id);
		const timers = Array.from({ length: 12 }, (_, index) =>
			setTimeout(
				() => setDisplayChips(initialChips + Math.round((request.amount * (index + 1)) / 12)),
				1030 + index * 54,
			),
		);
		return () => timers.forEach(clearTimeout);
	}, [request]);
	useEffect(() => {
		if (!request) return;
		walletOpacity.setValue(0);
		const animation = Animated.sequence([
			Animated.timing(walletOpacity, { toValue: 1, duration: 180, useNativeDriver: true }),
			Animated.delay(1560),
			Animated.timing(walletOpacity, { toValue: 0, duration: 180, useNativeDriver: true }),
		]);
		animation.start();
		return () => animation.stop();
	}, [request, walletOpacity]);
	if (!request) return null;
	const visibleChips = displayRequestId === request.id ? displayChips : request.initialChips;
	// iOS converts its window points against the actual fitted Spine view, including its bounds offset.
	// The other renderers receive the centre-origin skeleton coordinates they already expect.
	const webScale = Math.min(width / 390, height / 844) * 0.95;
	const skeletonPoint = (point: Point) => ({
		x:
			Platform.OS === 'ios'
				? point.x
				: (point.x - width / 2) / (Platform.OS === 'web' ? webScale : 1),
		y:
			Platform.OS === 'ios'
				? point.y
				: (height / 2 - point.y) / (Platform.OS === 'web' ? webScale : 1),
	});
	const source = skeletonPoint(request.source);
	const wallet = skeletonPoint(request.wallet);
	const walletScale = request.wallet.scale ?? 1;
	const scaledChipCenter = {
		x: 161 / 2 + (HEADER_WALLET_CHIP_CENTER.x - 161 / 2) * walletScale,
		y: 43 / 2 + (HEADER_WALLET_CHIP_CENTER.y - 43 / 2) * walletScale,
	};

	const content = (
		<View nativeID='rn-spine-root-daily-mission-claim' pointerEvents='none' style={styles.root}>
			<Animated.View
				style={[
					styles.wallet,
					{
						left: request.wallet.x - scaledChipCenter.x,
						top: request.wallet.y - scaledChipCenter.y + 8,
						opacity: walletOpacity,
						transform: [{ scale: walletScale }],
					},
				]}
			>
				<HeaderWallet overlay chipsOverride={visibleChips} />
			</Animated.View>
			<SpineView
				key={request.id}
				skeletonFile={SPINE_ASSETS.dailyMissionClaim.skeletonFile}
				atlasFile={SPINE_ASSETS.dailyMissionClaim.atlasFile}
				animationName={SPINE_ASSETS.dailyMissionClaim.animations.collect}
				loop={false}
				playing
				useAnimationBounds={false}
				sourceX={source.x}
				sourceY={source.y}
				useSourcePosition
				walletX={wallet.x}
				walletY={wallet.y}
				useWalletPosition
				sharedCanvasGroup={Platform.OS === 'web' ? 'daily-mission-claim' : undefined}
				style={styles.spine}
			/>
		</View>
	);

	return fullScreen ? (
		<Modal transparent visible animationType='none' statusBarTranslucent navigationBarTranslucent>
			{content}
		</Modal>
	) : (
		content
	);
};

const styles = StyleSheet.create({
	root: { ...StyleSheet.absoluteFillObject, zIndex: 1_000_000, elevation: 1_000_000 },
	spine: { width: '100%', height: '100%' },
	wallet: { position: 'absolute', zIndex: 1 },
});
